import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from './ui/dropdown-menu';
import { Scissors, User, LogOut, BarChart3, Plus, Home, Globe, Users, Bell, Check, Trophy, XCircle, Menu, X, Sun, Moon, Shield, CreditCard } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import apiClient from '../lib/api';
import LanguageSelector from './LanguageSelector';

const Navbar = () => {
  const { user, logout, notifications, unreadCount, markNotificationRead, markAllNotificationsRead } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pendingPayoutsCount, setPendingPayoutsCount] = useState(0);
  const [hasSettlementOrg, setHasSettlementOrg] = useState(true); // PayPal/Settlements nur in Settlement-Orgs

  React.useEffect(() => {
    const fetchPayoutsCount = async () => {
      try {
        const response = await apiClient.get('/payouts/overview');
        const toPayCount = response.data?.to_pay?.length || 0;
        const toConfirmCount = response.data?.to_confirm?.length || 0;
        setPendingPayoutsCount(toPayCount + toConfirmCount);
      } catch (err) {
        console.error('Failed to fetch payouts count in navbar:', err);
      }
    };

    if (user && (user.is_confirmed || user.is_admin)) {
      fetchPayoutsCount();
      apiClient.get('/organizations')
        .then(res => setHasSettlementOrg((res.data || []).some(o => o.membership === 'member' && o.settlement_enabled)))
        .catch(() => {});
      const interval = setInterval(fetchPayoutsCount, 120000);
      return () => clearInterval(interval);
    }
  }, [user]);

  if (!user) return null;

  // Hide Navbar on pending confirmation or email verification pages
  const isPending = !user.is_confirmed && !user.is_admin;
  const isEmailUnverified = !user.email_verified;
  
  if (isPending || isEmailUnverified) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = (notif) => {
    markNotificationRead(notif.id);
    if (notif.market_id) {
      navigate(`/market/${notif.market_id}`);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'bet_won': return <Trophy className="w-4 h-4 text-green-400" />;
      case 'bet_lost': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'friend_request': return <Users className="w-4 h-4 text-blue-400" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const NavItems = () => (
    <>
      <Link to="/markets" onClick={() => setIsMobileMenuOpen(false)}>
        <Button variant={location.pathname === '/markets' ? 'secondary' : 'ghost'} size="sm" className="gap-2 w-full justify-start sm:w-auto">
          <BarChart3 className="w-4 h-4" />
          <span>{t('markets')}</span>
        </Button>
      </Link>

      <Link to="/create-market" onClick={() => setIsMobileMenuOpen(false)}>
        <Button variant={location.pathname === '/create-market' ? 'secondary' : 'ghost'} size="sm" className="gap-2 w-full justify-start sm:w-auto">
          <Plus className="w-4 h-4" />
          <span>{t('create')}</span>
        </Button>
      </Link>

      <Link to="/friends" onClick={() => setIsMobileMenuOpen(false)}>
        <Button variant={location.pathname === '/friends' ? 'secondary' : 'ghost'} size="sm" className="gap-2 w-full justify-start sm:w-auto">
          <Users className="w-4 h-4" />
          <span>{t('friends')}</span>
        </Button>
      </Link>

      {hasSettlementOrg && (
      <Link to="/settlements" onClick={() => setIsMobileMenuOpen(false)}>
        <Button variant={location.pathname === '/settlements' ? 'secondary' : 'ghost'} size="sm" className="gap-2 w-full justify-start sm:w-auto relative">
          <CreditCard className="w-4 h-4" />
          <span>{t('settlements')}</span>
          {pendingPayoutsCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-600 text-white leading-none">
              {pendingPayoutsCount}
            </span>
          )}
        </Button>
      </Link>
      )}
    </>
  );

  return (
    <>
      <nav className="navbar" data-testid="navbar">
        <div className="navbar-content">
        <Link to="/dashboard" className="logo" data-testid="logo-link">
          <Scissors className="logo-icon w-8 h-8 text-red-500" />
          <span className="hidden xs:inline">Scherenmarket</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-2">
          <NavItems />
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {(() => {
            const totalProfit = user?.total_profit || 0;
            const totalLoss = user?.total_loss || 0;
            const netResult = totalProfit - totalLoss;
            const isPositive = netResult >= 0;
            const formattedValue = Math.abs(netResult).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return (
              <div className="balance-display text-sm sm:text-base px-2 sm:px-4 whitespace-nowrap" data-testid="balance-display">
                <span className={isPositive ? 'text-green-500 dark:text-green-400 font-bold' : 'text-red-500 dark:text-red-400 font-bold'}>
                  {isPositive ? '+' : '−'}
                </span>
                <span className={`number-highlight ${isPositive ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                  {formattedValue}{"\u00A0"}€
                </span>
              </div>
            );
          })()}

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-8 w-8 sm:h-9 sm:w-9" data-testid="notifications-trigger">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] sm:w-80 max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="font-medium">{language === 'de' ? 'Benachrichtigungen' : 'Notifications'}</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllNotificationsRead}>
                    <Check className="w-4 h-4 mr-1" />
                    {language === 'de' ? 'Alle gelesen' : 'Mark all read'}
                  </Button>
                )}
              </div>
              <DropdownMenuSeparator />
              {notifications.length > 0 ? (
                notifications.slice(0, 10).map(notif => (
                  <DropdownMenuItem 
                    key={notif?.id || Math.random()} 
                    className={`cursor-pointer p-3 ${!notif?.read ? 'bg-secondary/50' : ''}`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <div className="flex gap-3 w-full">
                      {getNotificationIcon(notif?.type)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{notif?.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{notif?.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {notif?.created_at ? new Date(notif.created_at).toLocaleDateString() : ''}
                        </p>
                      </div>
                      {!notif?.read && <div className="w-2 h-2 bg-red-500 rounded-full shrink-0" />}
                    </div>
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-muted-foreground text-sm">
                  {language === 'de' ? 'Keine Benachrichtigungen' : 'No notifications'}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="hidden md:flex text-muted-foreground hover:text-foreground h-8 w-8 sm:h-9 sm:w-9"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          <LanguageSelector className="hidden md:flex" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 rounded-full p-0 overflow-hidden" data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
                  <AvatarImage src={user?.avatar_url} alt={user?.username} className="object-cover" />
                  <AvatarFallback className="bg-red-600/20 text-red-500 font-bold text-xs">
                    {user?.username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1 text-sm font-medium">{user?.username}</div>
              <div className="px-2 py-1 text-xs text-muted-foreground flex items-center gap-1.5">
                <span>{LANGUAGES[language] || language}</span>
              </div>
              {!user?.email_verified && (
                <div className="px-2 py-1">
                  <Badge variant="outline" className="text-yellow-500 border-yellow-500 text-[10px]">
                    {language === 'de' ? 'E-Mail nicht verifiziert' : 'Email not verified'}
                  </Badge>
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/dashboard" className="cursor-pointer" data-testid="menu-dashboard">
                  <Home className="w-4 h-4 mr-2" />
                  {t('dashboard')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/profile" className="cursor-pointer" data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" />
                  {t('profile')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/friends" className="cursor-pointer" data-testid="menu-friends">
                  <Users className="w-4 h-4 mr-2" />
                  {t('friends')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/organizations" className="cursor-pointer" data-testid="menu-organizations">
                  <Globe className="w-4 h-4 mr-2" />
                  <span>Organisationen</span>
                </Link>
              </DropdownMenuItem>
              {hasSettlementOrg && (
              <DropdownMenuItem asChild>
                <Link to="/settlements" className="cursor-pointer" data-testid="menu-settlements">
                  <CreditCard className="w-4 h-4 mr-2" />
                  <span>{t('settlements')}</span>
                  {pendingPayoutsCount > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-600 text-white leading-none">
                      {pendingPayoutsCount}
                    </span>
                  )}
                </Link>
              </DropdownMenuItem>
              )}
              {user?.is_admin && (
                <DropdownMenuItem asChild>
                  <Link to="/admin" className="cursor-pointer text-blue-500">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {/* Mobile settings links */}
              <DropdownMenuItem onClick={toggleTheme} className="flex md:hidden cursor-pointer">
                {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {language === 'de' ? 'Design wechseln' : 'Toggle Theme'}
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer">
                  <Globe className="w-4 h-4 mr-2" />
                  <span>{language === 'de' ? 'Sprache wechseln' : 'Switch Language'}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="max-h-60 overflow-y-auto">
                    {Object.entries(LANGUAGES).map(([code, name]) => (
                      <DropdownMenuItem
                        key={code}
                        onClick={() => setLanguage(code)}
                        className={`cursor-pointer justify-between ${language === code ? 'bg-secondary font-semibold' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{name}</span>
                        </div>
                        {language === code && <Check className="w-4 h-4 text-green-500 shrink-0" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSeparator className="md:hidden" />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-500" data-testid="menu-logout">
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>

    {/* Mobile Bottom Navigation Bar */}
    <div className="mobile-bottom-nav">
      <Link to="/dashboard" className={`flex flex-col items-center gap-1 transition-colors ${location.pathname === '/dashboard' ? 'text-red-500 font-semibold' : 'text-muted-foreground hover:text-foreground'}`}>
        <Home className="w-5 h-5" />
        <span className="text-[10px] font-medium">{t('dashboard')}</span>
      </Link>
      <Link to="/markets" className={`flex flex-col items-center gap-1 transition-colors ${location.pathname === '/markets' ? 'text-red-500 font-semibold' : 'text-muted-foreground hover:text-foreground'}`}>
        <BarChart3 className="w-5 h-5" />
        <span className="text-[10px] font-medium">{t('markets')}</span>
      </Link>
      <Link to="/create-market" className="flex flex-col items-center -mt-6">
        <div className="w-11 h-11 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center text-white shadow-lg shadow-red-600/30 border-4 border-background transition-transform active:scale-95">
          <Plus className="w-5 h-5" />
        </div>
        <span className="text-[10px] font-medium text-muted-foreground mt-1">{t('create')}</span>
      </Link>
      {hasSettlementOrg && (
      <Link to="/settlements" className={`flex flex-col items-center gap-1 transition-colors relative ${location.pathname === '/settlements' ? 'text-red-500 font-semibold' : 'text-muted-foreground hover:text-foreground'}`}>
        <div className="relative">
          <CreditCard className="w-5 h-5" />
          {pendingPayoutsCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-600 rounded-full text-[8px] flex items-center justify-center text-white font-bold leading-none">
              {pendingPayoutsCount}
            </span>
          )}
        </div>
        <span className="text-[10px] font-medium text-center">{t('settlements')}</span>
      </Link>
      )}
      <Link to="/profile" className={`flex flex-col items-center gap-1 transition-colors ${location.pathname === '/profile' ? 'text-red-500 font-semibold' : 'text-muted-foreground hover:text-foreground'}`}>
        <User className="w-5 h-5" />
        <span className="text-[10px] font-medium">{t('profile')}</span>
      </Link>
    </div>
  </>
  );
};

export default Navbar;
